import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import cv from "../components/CV/Mirzaherenda.pdf";
import { useInView } from "framer-motion";
import { Eyebrow, Reveal } from "./motion";

const TIMELINE = [
  {
    period: "2013 — 2017",
    title: "HS of Electrical Engineering",
    place: "Sarajevo",
    body: "First contact with circuits, logic and code. The habit of taking things apart to understand them started here.",
  },
  {
    period: "2022 — 2023",
    title: "Paragon Full Stack Program",
    place: "Intensive bootcamp",
    body: "Went all-in on JavaScript, React and modern tooling — shipping real projects end to end, from database to interface.",
  },
  {
    period: "2024 — Present",
    title: "International Burch University",
    place: "Software Engineering",
    body: "Deepening the fundamentals: algorithms, architecture and systems thinking, while building production mobile apps alongside.",
  },
];

const STATS = [
  { value: 2, suffix: "", label: "Apps published" },
  { value: 10, suffix: "", label: "Technologies used" },
  { value: 4, suffix: "+", label: "Years coding" },
  { value: 100, suffix: "%", label: "Client focus" },
];

function useCountUp(value, active, duration = 1200) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setN(Math.round(value * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, value, duration]);
  return n;
}

function Stat({ value, suffix, label }) {
  const ref = useRef(null);
  const visible = useInView(ref, { once: true, amount: 0.4 });
  const n = useCountUp(value, visible);
  return (
    <div
      ref={ref}
      className="h-full flex flex-col justify-between rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm p-5 transition-transform duration-500 hover:-translate-y-1 sm:p-6"
      style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
    >
      <p className="text-3xl font-bold text-transparent bg-gradient-to-r from-primary-400 to-sky-400 bg-clip-text font-display sm:text-4xl">
        {n}
        {suffix}
      </p>
      <p className="mt-2 text-xs leading-snug text-slate-500 sm:text-sm">
        {label}
      </p>
    </div>
  );
}

const AboutMe = () => {
  const navigate = useNavigate();

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = cv;
    link.download = "MIRZAHERENDACV.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section
      id="about-us"
      className="relative bg-dark-bg px-4 py-24 sm:px-6 lg:py-32"
    >
      <div className="mx-auto max-w-6xl 2xl:max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <Reveal>
            <Eyebrow>About me</Eyebrow>
            <h2 className="mt-6 text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-[1.05] text-transparent bg-gradient-to-r from-primary-400 to-sky-400 bg-clip-text font-display">
              Building software that people actually enjoy using.
            </h2>
          </Reveal>

          <div>
            <Reveal delay={80}>
              <p className="text-lg leading-relaxed text-slate-400">
                I'm a software developer specializing in{" "}
                <span className="text-slate-200">React.js</span> and{" "}
                <span className="text-slate-200">React Native</span>. I create
                dynamic, responsive web and mobile applications with an
                obsessive focus on performance and user experience.
              </p>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-5 leading-relaxed text-slate-400">
                My work sits where clean engineering meets thoughtful design:
                interfaces that feel fast, data that stays readable, and
                products that keep working as they grow. Every project is built
                to fit the client's real needs — not a template.
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              {STATS.map((s, i) => (
                <Reveal key={s.label} delay={i * 90} className="h-full">
                  <Stat {...s} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div id="journey" className="mt-24 scroll-mt-28 lg:mt-32">
          <Reveal>
            <Eyebrow>The journey</Eyebrow>
          </Reveal>
          <ol className="mt-10 grid gap-4 md:grid-cols-3 md:gap-5">
            {TIMELINE.map((item, i) => (
              <Reveal as="li" key={item.title} delay={i * 120}>
                <div className="group relative h-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm p-6 transition-all duration-500 hover:-translate-y-1.5 hover:border-primary-500/30 hover:shadow-2xl sm:p-7">
                  <div
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-px opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                      background:
                        "linear-gradient(to right, transparent, #38bdf8, transparent)",
                    }}
                  />
                  <div className="flex items-center gap-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-primary-500/30 bg-primary-500/10 font-mono text-xs text-primary-400">
                      0{i + 1}
                    </span>
                    <span className="font-mono text-[11px] tracking-[0.18em] text-slate-500 uppercase">
                      {item.period}
                    </span>
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-slate-100 font-display">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm text-primary-400">{item.place}</p>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">
                    {item.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default AboutMe;
